import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SaleSnapshot } from 'src/app/docs/export/sale-stock-plan';

export type TerminalPaymentAttemptStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'DECLINED'
  | 'CANCELLED'
  | 'TIMEOUT'
  | 'ERROR';

export interface TerminalPaymentAttempt {
  id: number;
  requestId: string;
  status: TerminalPaymentAttemptStatus;
  amount: number;
  currency: string;
  orderId?: number | null;
  invoiceId?: number | null;
  exportDocId?: number | null;
  paymentId?: number | null;
  paymentIds?: number[] | null;
  operationId?: number | null;
  operation_id?: number | null;
  bookingStatus?: 'NOT_REQUIRED' | 'PENDING' | 'BOOKED' | 'FAILED';
  booking_status?: 'NOT_REQUIRED' | 'PENDING' | 'BOOKED' | 'FAILED';
  bookingError?: string | null;
  booking_error?: string | null;
  responseCode?: string | null;
  responseMessage?: string | null;
  error?: string | null;
  isFinal: boolean;
  agentName?: string;
  terminal_profile_id?: number | null;
  rawRequest?: { terminalProfileId?: number; [key: string]: unknown };
}

export interface TerminalSaleRecovery {
  attempt: TerminalPaymentAttempt;
  sale_snapshot: SaleSnapshot;
}

export interface StartTerminalPaymentPayload {
  request_id?: string;
  amount?: number;
  agentName?: string;
  warehouseId?: number;
  terminal_profile_id?: number;
  provider_batch_id?: string;
  exportDocId?: number;
  fiscal?: boolean;
  sale_snapshot?: SaleSnapshot;
}

export interface TerminalProfile {
  id: number;
  provider: string;
  label: string;
  merchantId: string;
  terminalId: string;
  agentDeviceId?: string | null;
  warehouseId: number;
  moneyContainerId?: number | null;
  currentProviderBatchId?: string | null;
  isActive: boolean;
}

export interface CreateTerminalProfilePayload {
  provider: string;
  label: string;
  merchantId: string;
  terminalId: string;
  agentDeviceId?: string;
  warehouseId: number;
  moneyContainerId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class TerminalPaymentService {
  constructor(private readonly http: HttpClient) {}

  getSaleRecovery() {
    return this.http.get<TerminalSaleRecovery[]>(`${environment.BACKEND}/terminal-payments/sale-recovery`);
  }

  startStandalonePayment(payload: StartTerminalPaymentPayload) {
    return this.http.post<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/start`,
      payload
    );
  }

  getProfiles(warehouseId?: number, active = true) {
    const params: Record<string, string> = { active: String(active) };
    if (warehouseId) params.warehouse_id = String(warehouseId);
    return this.http
      .get<TerminalProfile[] | { items: TerminalProfile[] }>(
        `${environment.BACKEND}/terminal-payments/profiles`,
        { params }
      )
      .pipe(map((response) => (Array.isArray(response) ? response : response?.items || [])));
  }

  createProfile(payload: CreateTerminalProfilePayload) {
    return this.http.post<TerminalProfile>(
      `${environment.BACKEND}/terminal-payments/profiles`,
      payload
    );
  }

  startOrderPayment(orderId: number, payload: StartTerminalPaymentPayload) {
    return this.http.post<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/order/${orderId}/start`,
      payload
    );
  }

  startInvoicePayment(invoiceId: number, payload: StartTerminalPaymentPayload) {
    return this.http.post<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/invoice/${invoiceId}/start`,
      payload
    );
  }

  getAttempt(attemptId: number) {
    return this.http.get<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/${attemptId}`
    );
  }

  cancelAttempt(attemptId: number) {
    return this.http.post<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/${attemptId}/cancel`,
      {}
    );
  }

  retryBooking(attemptId: number) {
    return this.http.post<TerminalPaymentAttempt>(
      `${environment.BACKEND}/terminal-payments/${attemptId}/retry-booking`,
      {}
    );
  }
}
